import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  ArrowUp,
  ArrowDown,
  Minus,
  CheckCircle2,
  AlertTriangle,
  Sparkles,
} from "lucide-react";

interface AnalysisResult {
  score: number;
  nivel: "Baixo" | "Médio" | "Alto";
  pontos_fortes: string[];
  lacunas: string[];
  sugestoes: string[];
  resumo: string;
}

interface ComparisonPanelProps {
  previous: AnalysisResult;
  current: AnalysisResult;
}

export function ComparisonPanel({ previous, current }: ComparisonPanelProps) {
  const delta = current.score - previous.score;

  const resolvedGaps = previous.lacunas.filter(
    (gap) => !current.lacunas.some((g) => g.toLowerCase() === gap.toLowerCase())
  );
  const persistentGaps = current.lacunas.filter((gap) =>
    previous.lacunas.some((g) => g.toLowerCase() === gap.toLowerCase())
  );
  const newGaps = current.lacunas.filter(
    (gap) => !previous.lacunas.some((g) => g.toLowerCase() === gap.toLowerCase())
  );
  const newStrengths = current.pontos_fortes.filter(
    (s) => !previous.pontos_fortes.some((p) => p.toLowerCase() === s.toLowerCase())
  );

  const getDeltaColor = () => {
    if (delta > 0) return "text-emerald-500";
    if (delta < 0) return "text-red-500";
    return "text-muted-foreground";
  };

  const getDeltaIcon = () => {
    if (delta > 0) return <ArrowUp className="h-5 w-5 text-emerald-500" />;
    if (delta < 0) return <ArrowDown className="h-5 w-5 text-red-500" />;
    return <Minus className="h-5 w-5 text-muted-foreground" />;
  };

  const getScoreBarColor = (score: number) => {
    if (score >= 70) return "bg-emerald-500";
    if (score >= 40) return "bg-amber-500";
    return "bg-red-500";
  };

  return (
    <div className="space-y-4">
      <Card className="border-primary/20 bg-primary/5">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold flex items-center gap-2 text-primary">
            <Sparkles className="h-4 w-4" />
            Evolução do Currículo
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Score comparison */}
          <div className="flex items-center justify-center gap-4">
            <div className="text-center">
              <p className="text-xs text-muted-foreground mb-1">Anterior</p>
              <span className="text-3xl font-bold text-muted-foreground">{previous.score}</span>
            </div>
            <div className="flex items-center gap-1">
              {getDeltaIcon()}
              <span className={`text-2xl font-bold ${getDeltaColor()}`}>
                {delta > 0 ? `+${delta}` : delta}
              </span>
            </div>
            <div className="text-center">
              <p className="text-xs text-muted-foreground mb-1">Atual</p>
              <span className="text-3xl font-bold text-foreground">{current.score}</span>
            </div>
          </div>

          {/* Progress bars */}
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <span className="text-xs text-muted-foreground w-16 shrink-0">Antes</span>
              <div className="flex-1 h-2 rounded-full bg-secondary overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${getScoreBarColor(previous.score)}`}
                  style={{ width: `${previous.score}%` }}
                />
              </div>
              <span className="text-xs text-muted-foreground w-8 text-right">{previous.score}</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs text-muted-foreground w-16 shrink-0">Depois</span>
              <div className="flex-1 h-2 rounded-full bg-secondary overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${getScoreBarColor(current.score)}`}
                  style={{ width: `${current.score}%` }}
                />
              </div>
              <span className="text-xs text-muted-foreground w-8 text-right">{current.score}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Resolved gaps */}
        {resolvedGaps.length > 0 && (
          <Card className="border-emerald-500/20">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold flex items-center gap-2 text-emerald-600">
                <CheckCircle2 className="h-4 w-4" />
                Lacunas Resolvidas
                <Badge variant="outline" className="bg-emerald-500/10 text-emerald-700 border-emerald-500/30 ml-auto">
                  {resolvedGaps.length}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <ul className="space-y-2">
                {resolvedGaps.map((item, i) => (
                  <li key={i} className="text-sm text-muted-foreground flex gap-2 line-through opacity-70">
                    <span className="text-emerald-500 shrink-0 mt-0.5 no-underline">✓</span>
                    {item}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {/* Persistent + new gaps */}
        {(persistentGaps.length > 0 || newGaps.length > 0) && (
          <Card className="border-amber-500/20">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold flex items-center gap-2 text-amber-600">
                <AlertTriangle className="h-4 w-4" />
                Lacunas Restantes
                <Badge variant="outline" className="bg-amber-500/10 text-amber-700 border-amber-500/30 ml-auto">
                  {persistentGaps.length + newGaps.length}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <ul className="space-y-2">
                {persistentGaps.map((item, i) => (
                  <li key={i} className="text-sm text-muted-foreground flex gap-2">
                    <span className="text-amber-500 shrink-0 mt-0.5">⚠</span>
                    {item}
                  </li>
                ))}
                {newGaps.map((item, i) => (
                  <li key={`new-${i}`} className="text-sm text-muted-foreground flex gap-2">
                    <span className="text-red-500 shrink-0 mt-0.5">●</span>
                    {item}
                    <Badge variant="outline" className="text-[10px] px-1 py-0 bg-red-500/10 text-red-600 border-red-500/30">novo</Badge>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {/* New strengths */}
        {newStrengths.length > 0 && (
          <Card className="border-blue-500/20">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold flex items-center gap-2 text-blue-600">
                <Sparkles className="h-4 w-4" />
                Novos Pontos Fortes
                <Badge variant="outline" className="bg-blue-500/10 text-blue-700 border-blue-500/30 ml-auto">
                  {newStrengths.length}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <ul className="space-y-2">
                {newStrengths.map((item, i) => (
                  <li key={i} className="text-sm text-muted-foreground flex gap-2">
                    <span className="text-blue-500 shrink-0 mt-0.5">★</span>
                    {item}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
